import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Button,
  Badge,
  Form,
  InputGroup,
  Pagination,
  Modal,
  Alert,
  Spinner,
} from "react-bootstrap";
import {
  FaStore,
  FaSearch,
  FaEye,
  FaTrash,
  FaCheck,
  FaTimes,
  FaChartBar,
  FaDownload,
  FaFileExcel,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./auth/AuthContext";
import * as XLSX from "xlsx";

const VendorDashboard = () => {
  const navigate = useNavigate();
  const { isSubadmin } = useAuth();
  const [vendors, setVendors] = useState([]);
  const [filteredVendors, setFilteredVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [verificationFilter, setVerificationFilter] = useState("all");
  const [pincodeFilter, setPincodeFilter] = useState("");
  const [societyNameFilter, setSocietyNameFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(50);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [dateSortOrder, setDateSortOrder] = useState("newest");
  const [actionLoading, setActionLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  // ← FIXED: removed stray `const securityDeposit = v.securityDeposit || {};`
  //   that caused a ReferenceError because `v` is undefined at component scope.
  const [summary, setSummary] = useState({ totalBookings: 0, totalEarnings: 0 });
  const [taxRates, setTaxRates] = useState({ platformFee: 0.05 });

  const fetchingRef = useRef(false);

  // ── Load vendors, tax rates, and summary in a single batch ─────────────
  useEffect(() => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;

    const fetchAllDashboardData = async () => {
      try {
        setLoading(true);
        setErrorMessage("");

        const rawBase = process.env.REACT_APP_API_BASE_URL || "http://localhost:3000";
        const base = rawBase.replace(/\/$/, "");
        const adminToken = localStorage.getItem("adminToken") || sessionStorage.getItem("adminToken");

        const headers = {
          "Content-Type": "application/json",
          ...(adminToken && { Authorization: `Bearer ${adminToken}` }),
        };

        // Single parallel batch fetch for tax-rates, vendors, and summary
        const results = await Promise.allSettled([
          fetch(`${base}/api/admin/config/tax-rates`, { headers }),
          fetch(`${base}/api/vendors?limit=100`, { headers }),
          fetch(`${base}/api/bookings/summary`, { headers }),
        ]);

        const [taxRes, vendorsRes, summaryRes] = results.map((r) =>
          r.status === "fulfilled" && r.value && r.value.ok ? r.value : null
        );

        // 1. Process Tax Rates
        let fetchedTaxRates = { platformFee: 0.05 };
        if (taxRes) {
          try {
            const taxData = await taxRes.json();
            fetchedTaxRates = taxData.taxRates || { platformFee: 0.05 };
            setTaxRates(fetchedTaxRates);
          } catch (_) { }
        }

        // 2. Process Summary
        if (summaryRes) {
          try {
            const summaryData = await summaryRes.json();
            setSummary({
              totalBookings: summaryData.totalBookings || 0,
              totalEarnings: summaryData.totalRevenue || 0,
            });
          } catch (_) { }
        }

        // 3. Process Vendors
        if (!vendorsRes) {
          throw new Error("Failed to load vendor data from API");
        }

        const data = await vendorsRes.json();
        const rawVendors = Array.isArray(data) ? data : data.vendors || [];

        const normalized = rawVendors.map((v, idx) => {
          const personal = v.vendor?.personal_info || {};
          const contactInfo = v.contactInfo || {};
          const categories = v.vendor?.categories || [];

          const serviceNames = Array.isArray(v.services)
            ? v.services
            : categories.flatMap((c) =>
              Array.isArray(c.services)
                ? c.services.map((s) => s.service_name || s.name || "Service")
                : []
            );

          const subscription = v.subscription || {};
          const subscriptionStatus = subscription.status || "inactive";
          const subscriptionPlan = subscription.plan || "none";
          const subscriptionEndDate = subscription.endDate || null;

          let daysRemaining = null;
          if (subscriptionEndDate) {
            const end = new Date(subscriptionEndDate);
            const today = new Date();
            daysRemaining = Math.ceil((end - today) / (1000 * 60 * 60 * 24));
          }

          const sd = v.securityDeposit || {};
          const securityDepositPaid = sd.paid === true;
          const securityDepositAmount = sd.amount ?? 0;
          const securityDepositPaidAt = sd.paidAt || null;

          let isVerified = false;
          if (v.verification?.isVerified === true) isVerified = true;
          else if (v.verificationStatus === "approved") isVerified = true;
          else if (v.verification?.status === "approved") isVerified = true;

          const platformFeeRate = fetchedTaxRates.platformFee || 0.05;

          return {
            id: v.id || v.vendorId || v.uid || idx,
            name: personal.name || v.businessName || "",
            email: personal.email || v.email || contactInfo.email || "",
            phone: personal.phone || v.phone || contactInfo.phone || "",
            category: v.businessCategory || v.category || categories[0]?.category_name || "",
            services: serviceNames.length ? serviceNames : ["General"],
            status: v.status || "active",
            password: personal.password_plain || personal.password || v.password || "—",
            joinDate: v.createdAt || v.joinDate || new Date().toISOString(),
            location: personal.address || v.address || "—",
            verified: isVerified,
            verificationStatus: v.verificationStatus || v.verification?.status || "pending",
            bookingCount: v.bookingCount || 0,
            bookingAmount: v.earnings || 0,
            earnings: v.earnings ? Math.max(0, v.earnings - v.earnings * platformFeeRate) : 0,
            tax: v.earnings ? Math.round(v.earnings * platformFeeRate * 100) / 100 : 0,
            pincode: personal.pincode || v.pincode || "—",
            societyName: personal.societyName || v.societyName || "—",
            subscriptionStatus,
            subscriptionPlan,
            subscriptionEndDate,
            daysRemaining,
            securityDepositPaid,
            securityDepositAmount,
            securityDepositPaidAt,
          };
        });

        setVendors(normalized);
        setFilteredVendors(normalized);
      } catch (err) {
        console.error("Vendor load error:", err);
        setErrorMessage(err.message || "Failed to load vendors");
      } finally {
        setLoading(false);
      }
    };

    fetchAllDashboardData().finally(() => {
      fetchingRef.current = false;
    });
  }, []);

  // ── Filter + sort ───────────────────────────────────────────────────────────
  useEffect(() => {
    let filtered = vendors.filter((vendor) => {
      const term = searchTerm.toLowerCase();
      const matchesSearch =
        term === "" ||
        vendor.name?.toLowerCase().includes(term) ||
        vendor.email?.toLowerCase().includes(term) ||
        vendor.category?.toLowerCase().includes(term);
      const matchesVerification =
        verificationFilter === "all" ||
        (verificationFilter === "verified" && vendor.verified) ||
        (verificationFilter === "unverified" && !vendor.verified);
      const matchesPincode =
        pincodeFilter === "" || vendor.pincode?.toString().startsWith(pincodeFilter);
      const matchesSocietyName =
        societyNameFilter === "" ||
        vendor.societyName?.toLowerCase().includes(societyNameFilter.toLowerCase());
      return matchesSearch && matchesVerification && matchesPincode && matchesSocietyName;
    });

    filtered.sort((a, b) => {
      const dateA = new Date(a.joinDate);
      const dateB = new Date(b.joinDate);
      return dateSortOrder === "newest" ? dateB - dateA : dateA - dateB;
    });

    setFilteredVendors(filtered);
    setCurrentPage(1);
  }, [vendors, searchTerm, verificationFilter, pincodeFilter, societyNameFilter, dateSortOrder]);

  // ── Pagination ──────────────────────────────────────────────────────────────
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentVendors = filteredVendors.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredVendors.length / itemsPerPage);

  // ── Stats ───────────────────────────────────────────────────────────────────
  const totalBookings = vendors.reduce((sum, v) => sum + (v.bookingCount || 0), 0);
  const totalEarnings = vendors.reduce((sum, v) => sum + (v.earnings || 0), 0);
  const verifiedVendors = vendors.filter((v) => v.verified).length;
  const unverifiedVendors = vendors.filter((v) => !v.verified).length;

  const stats = {
    total: vendors.length, verified: verifiedVendors,
    unverified: unverifiedVendors, totalBookings, totalEarnings,
  };

  // ── Action handlers ─────────────────────────────────────────────────────────
  const handleViewVendor = (vendor) => navigate(`/vendor-info/${vendor.id}`);
  const handleDeleteVendor = (vendor) => { setSelectedVendor(vendor); setShowDeleteModal(true); };

  const confirmDelete = async () => {
    setActionLoading(true);
    try {
      const bases = Array.from(
        new Set(
          [process.env.REACT_APP_API_BASE_URL, "https://api.quvikme.com"].filter(Boolean),
        ),
      );
      let success = false;
      let lastErr = null;
      for (const base of bases) {
        try {
          const adminToken =
            localStorage.getItem("adminToken") || sessionStorage.getItem("adminToken");
          const headers = { "Content-Type": "application/json" };
          if (adminToken) headers["Authorization"] = `Bearer ${adminToken}`;
          const response = await fetch(`${base}/api/vendors/${selectedVendor.id}`, {
            method: "DELETE",
            headers,
          });
          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            lastErr = new Error(errorData.message || `Delete failed: ${response.status}`);
            continue;
          }
          success = true;
          break;
        } catch (error) { lastErr = error; continue; }
      }
      if (!success) throw lastErr || new Error("Failed to delete vendor from any endpoint");
      setVendors(vendors.filter((v) => v.id !== selectedVendor.id));
      setShowDeleteModal(false);
      setSelectedVendor(null);
      setErrorMessage("");
      setSuccessMessage(`Vendor "${selectedVendor.name}" has been deleted successfully.`);
      setTimeout(() => setSuccessMessage(""), 5000);
    } catch (error) {
      console.error("❌ Error deleting vendor:", error);
      setErrorMessage(`Failed to delete vendor: ${error.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  // ── Badge helpers ───────────────────────────────────────────────────────────
  const getVerificationBadge = (verified) =>
    verified
      ? <Badge bg="success"><FaCheck /> Verified</Badge>
      : <Badge bg="secondary"><FaTimes /> Unverified</Badge>;

  const getKycBadge = (status) => {
    switch (status) {
      case "approved": return <Badge bg="success">Approved</Badge>;
      case "pending": return <Badge bg="warning">Pending</Badge>;
      case "rejected": return <Badge bg="danger">Rejected</Badge>;
      default: return <Badge bg="secondary">Not Submitted</Badge>;
    }
  };

  const getSubscriptionBadge = (vendor) => {
    if (!vendor.subscriptionStatus || vendor.subscriptionPlan === "none")
      return <Badge bg="secondary">No Plan</Badge>;
    if (vendor.daysRemaining < 0)
      return <Badge bg="danger">Expired</Badge>;
    if (vendor.daysRemaining <= 3)
      return <Badge bg="warning">Expiring ({vendor.daysRemaining}d)</Badge>;
    return <Badge bg="success">Active ({vendor.subscriptionPlan})</Badge>;
  };

  // Security deposit — mirrors the badge logic in VendorInfo.js
  const getSecurityDepositBadge = (vendor) => {
    if (vendor.securityDepositPaid) {
      return (
        <Badge
          bg="success"
          title={
            vendor.securityDepositPaidAt
              ? `Paid on ${new Date(vendor.securityDepositPaidAt).toLocaleDateString()}`
              : "Paid"
          }
        >
          ✓ Paid
        </Badge>
      );
    }
    return <Badge bg="warning" text="dark">Unpaid</Badge>;
  };

  // ── Excel export ────────────────────────────────────────────────────────────
  const downloadVendorsReport = () => {
    const reportData = vendors.map((vendor) => ({
      "Vendor Name": vendor.name,
      Email: vendor.email,
      Phone: vendor.phone,
      Category: vendor.category,
      Services: vendor.services.join(", "),
      Location: vendor.location,
      "Society Name": vendor.societyName,
      "Join Date": new Date(vendor.joinDate).toLocaleDateString(),
      Bookings: vendor.bookingCount,
      "Booking Amount (₹)": vendor.bookingAmount,
      "Earnings (₹)": vendor.earnings,
      "Tax (₹)": vendor.tax,
      "Verification Status": vendor.verificationStatus,
      Verified: vendor.verified ? "Yes" : "No",
      "Security Deposit": vendor.securityDepositPaid ? "Paid" : "Unpaid",
      "Deposit Amount (₹)": vendor.securityDepositPaid ? vendor.securityDepositAmount : 0,
      "Deposit Paid On": vendor.securityDepositPaidAt
        ? new Date(vendor.securityDepositPaidAt).toLocaleDateString()
        : "—",
    }));
    const ws = XLSX.utils.json_to_sheet(reportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Vendors Report");
    XLSX.writeFile(wb, "vendors-report.xlsx");
  };

  const getRepeatVendors = () =>
    [...vendors.filter((v) => v.bookingCount >= 2)].sort(
      (a, b) => b.bookingCount - a.bookingCount,
    );

  // ── Render ──────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Container className="text-center py-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  return (
    <Container fluid>
      <Row className="mb-4">
        <Col>
          <h2><FaStore className="me-2" />Vendor Management Dashboard</h2>
          <p className="text-muted">Manage all registered vendors and their services</p>
        </Col>
      </Row>

      {errorMessage && (
        <Row className="mb-3">
          <Col>
            <Alert variant="danger" dismissible onClose={() => setErrorMessage("")}>
              <FaTimes className="me-2" />{errorMessage}
            </Alert>
          </Col>
        </Row>
      )}

      {successMessage && (
        <Row className="mb-3">
          <Col>
            <Alert variant="success" dismissible onClose={() => setSuccessMessage("")}>
              <FaCheck className="me-2" />{successMessage}
            </Alert>
          </Col>
        </Row>
      )}

      {/* Stats */}
      <Row className="g-3 mb-4">
        <Col xs={12} sm={6} md={3}>
          <Card className="text-center shadow-sm border-0 h-100">
            <Card.Body className="d-flex flex-column align-items-center justify-content-center p-3">
              <FaStore className="text-primary mb-2" size={28} />
              <h3 className="fw-bold mb-1">{stats.total}</h3>
              <p className="text-muted small mb-0">Total Vendors</p>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} sm={6} md={3}>
          <Card className="text-center shadow-sm border-0 h-100">
            <Card.Body className="d-flex flex-column align-items-center justify-content-between p-3">
              <div>
                <FaChartBar className="text-info mb-2" size={28} />
                <h3 className="fw-bold mb-1">{stats.totalBookings}</h3>
                <p className="text-muted small mb-0">Total Bookings</p>
              </div>
              <Button variant="outline-info" size="sm" className="w-100 mt-2" onClick={() => navigate("/bookings")}>
                <FaEye className="me-1" />View All
              </Button>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} sm={6} md={3}>
          <Card className="text-center shadow-sm border-0 h-100">
            <Card.Body className="d-flex flex-column align-items-center justify-content-center p-3">
              <FaDownload className="text-success mb-2" size={28} />
              <h3 className="fw-bold mb-1">₹{stats.totalEarnings.toLocaleString()}</h3>
              <p className="text-muted small mb-0">Total Earnings</p>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} sm={6} md={3}>
          <Card className="text-center shadow-sm border-0 h-100">
            <Card.Body className="d-flex flex-column align-items-center justify-content-center p-3">
              <FaCheck className="text-success mb-2" size={28} />
              <h3 className="fw-bold mb-1">{stats.verified}</h3>
              <p className="text-muted small mb-0">Verified Vendors</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Row className="g-3 mb-4">
        <Col xs={12} md={6} lg={4}>
          <InputGroup>
            <InputGroup.Text><FaSearch /></InputGroup.Text>
            <Form.Control
              placeholder="Search vendors by name, email, category..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
        </Col>
        <Col xs={12} sm={6} md={3} lg={2}>
          <Form.Control
            type="text"
            placeholder="Pincode filter"
            value={pincodeFilter}
            onChange={(e) => setPincodeFilter(e.target.value)}
          />
        </Col>
        <Col xs={12} sm={6} md={3} lg={2}>
          <Form.Control
            type="text"
            placeholder="Society filter"
            value={societyNameFilter}
            onChange={(e) => setSocietyNameFilter(e.target.value)}
          />
        </Col>
        <Col xs={12} sm={6} md={3} lg={2}>
          <Form.Select
            value={verificationFilter}
            onChange={(e) => setVerificationFilter(e.target.value)}
          >
            <option value="all">All Verification</option>
            <option value="verified">Verified</option>
            <option value="unverified">Unverified</option>
          </Form.Select>
        </Col>
        <Col xs={12} sm={6} md={3} lg={2}>
          <Form.Select
            value={dateSortOrder}
            onChange={(e) => setDateSortOrder(e.target.value)}
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
          </Form.Select>
        </Col>
      </Row>

      {/* Table */}
      <Card className="shadow-sm border-0">
        <Card.Header className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center gap-2 bg-light border-0 py-3">
          <h5 className="mb-0 fw-bold">All Vendors ({filteredVendors.length})</h5>
          <Button variant="outline-success" size="sm" onClick={downloadVendorsReport}>
            <FaFileExcel className="me-2" />Export to Excel
          </Button>
        </Card.Header>
        <Card.Body className="p-0">
          <Table responsive hover className="align-middle text-nowrap mb-0">
            <thead>
              <tr>
                <th>Actions</th>
                <th>Vendor</th>
                <th>Category</th>
                <th>Subscription</th>
                <th>Security Deposit</th>  {/* ← new column */}
                <th>Status</th>
                <th>Bookings</th>
                <th>KYC</th>
                <th>Joined</th>
              </tr>
            </thead>
            <tbody>
              {currentVendors.map((vendor) => (
                <tr key={vendor.id}>
                  <td>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="me-1 mb-1"
                      onClick={() => handleViewVendor(vendor)}
                    >
                      <FaEye />
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleDeleteVendor(vendor)}
                    >
                      <FaTrash />
                    </Button>
                  </td>

                  <td>
                    <strong>{vendor.name}</strong>
                    <br />
                    <small className="text-muted">{vendor.phone}</small>
                    <br />
                    <small className="text-muted">{vendor.email}</small>
                  </td>

                  <td>{vendor.category}</td>

                  <td>{getSubscriptionBadge(vendor)}</td>

                  {/* ── Security Deposit cell ────────────────────────────── */}
                  <td className="align-middle">
                    {getSecurityDepositBadge(vendor)}
                    {vendor.securityDepositPaid && vendor.securityDepositPaidAt && (
                      <div className="text-muted" style={{ fontSize: "0.73rem" }}>
                        {new Date(vendor.securityDepositPaidAt).toLocaleDateString()}
                      </div>
                    )}
                  </td>

                  <td>
                    <Badge bg={vendor.status === "active" ? "success" : "danger"}>
                      {vendor.status}
                    </Badge>
                  </td>

                  <td>
                    <Badge bg="primary">{vendor.bookingCount}</Badge>
                  </td>

                  <td>{getKycBadge(vendor.kycStatus)}</td>

                  <td>{new Date(vendor.joinDate).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </Table>

          {totalPages > 1 && (
            <div className="d-flex justify-content-center">
              <Pagination>
                <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                <Pagination.Prev onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1} />
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <Pagination.Item
                    key={page}
                    active={page === currentPage}
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </Pagination.Item>
                ))}
                <Pagination.Next onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages} />
                <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
              </Pagination>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* View Modal */}
      <Modal show={showViewModal} onHide={() => setShowViewModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Vendor Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedVendor && (
            <Row>
              <Col md={6}>
                <h6>Basic Information</h6>
                <p><strong>Name:</strong> {selectedVendor.name}</p>
                <p><strong>Email:</strong> {selectedVendor.email}</p>
                <p><strong>Phone:</strong> {selectedVendor.phone}</p>
                <p><strong>Location:</strong> {selectedVendor.location}</p>
                <p><strong>Category:</strong> {selectedVendor.category}</p>
                <p><strong>Verification:</strong> {getVerificationBadge(selectedVendor.verified)}</p>
                <p><strong>Join Date:</strong> {new Date(selectedVendor.joinDate).toLocaleDateString()}</p>
                <p>
                  <strong>Security Deposit:</strong>{" "}
                  {getSecurityDepositBadge(selectedVendor)}
                  {selectedVendor.securityDepositPaid && selectedVendor.securityDepositPaidAt && (
                    <span className="ms-2 text-muted small">
                      on {new Date(selectedVendor.securityDepositPaidAt).toLocaleDateString()}
                    </span>
                  )}
                </p>
              </Col>
              <Col md={6}>
                <h6>Services</h6>
                <div>
                  {selectedVendor.services.map((service, index) => (
                    <Badge key={index} bg="info" className="me-1 mb-1">{service}</Badge>
                  ))}
                </div>
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowViewModal(false)}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <strong>⚠️ Warning: This action will permanently delete:</strong>
          </div>
          <ul className="mb-3">
            <li>Vendor profile and all personal information</li>
            <li>All vendor bookings and booking history</li>
            <li>All reviews and ratings for this vendor</li>
            <li>All related data across the system</li>
          </ul>
          <div className="mb-3">
            Are you sure you want to delete <strong>{selectedVendor?.name}</strong>?
            <br />
            <strong className="text-danger">This action cannot be undone.</strong>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
          <Button variant="danger" onClick={confirmDelete} disabled={actionLoading}>
            {actionLoading ? <Spinner animation="border" size="sm" /> : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default VendorDashboard;